import { useQuery, useReactiveVar } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Skeleton } from '@mui/material';
import type React from 'react';
import { useRef } from 'react';
import { useDrag, useDrop, type XYCoord } from 'react-dnd';
import assetBasicInfo from 'src/api/assets/assetBasicInfo';
import { getSuperViewAugments, parseSuperView, type SuperViewResponse } from 'src/api/viewModels/data';
import { buildSuperView } from 'src/dynamoQueries/superView';
import MediaRouterComponent from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaRouterComponent';
import { getAssetDisplayName } from 'src/helpers/assetDisplayName';
import { getMediaTypeIcon } from 'src/helpers/mediaTypeIcon';
import viewStateVar from 'src/store/viewState';
import type Player from 'video.js/dist/types/player';
import usePlaylistItemStyles from './playlistItemStyles';

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export interface PlaylistItemRowProps {
  id: string;
  /** Display name — if provided, skips the metadata query */
  name?: string;
  index: number;
  mediatype?: string;
  /** react-dnd type string — must be unique per list context */
  dragType: string;
  onRemove: () => void;
  /** If provided, title becomes a clickable button */
  onTitleClick?: () => void;
  /** If provided, thumbnail becomes clickable */
  onThumbnailClick?: () => void;
  hoverCard: (fromIndex: number, toIndex: number) => void;
  moveCard: (id: string, toIndex: number) => void;
  /** Optional overlay content rendered inside the row (e.g., preview modal) */
  overlay?: React.ReactNode;
  /** When this matches `id`, the row plays a flash highlight animation */
  flashId?: string | null;
}

const PlaylistItemRow: React.FC<PlaylistItemRowProps> = ({
  id,
  name,
  index,
  mediatype,
  dragType,
  onRemove,
  onTitleClick,
  onThumbnailClick,
  hoverCard,
  moveCard,
  overlay,
  flashId,
}) => {
  const classes = usePlaylistItemStyles();
  const ref = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const viewState = useReactiveVar(viewStateVar);
  const viewComponentName = viewState.viewModels.library.ViewMediaCardMinimalInfo?.node?.componentName;

  const setPlayer = (_player: Player): void => {
    playerRef.current = _player;
  };

  // Always query SuperView — the single source of truth for display names.
  // Apollo cache makes this free for assets already visible in the grid.
  const {
    data: svData,
    loading: svLoading,
    error: svError,
  } = useQuery(buildSuperView(), {
    variables: { globalId: id, viewComponentName },
    skip: !viewComponentName,
  });
  // Resolved title from the backend augments sentinel; fall back to the first
  // configured field for view components that don't opt in to augments.
  const superViewTitle =
    getSuperViewAugments(svData as SuperViewResponse).title ??
    parseSuperView(svData as SuperViewResponse)?.[0]?.show ??
    undefined;

  // assetBasicInfo: lightweight fallback for both display name and mediatype icon.
  // Always fetched so contentObject.name is available when SuperView or drag data lack a name.
  // Apollo cache makes this free for assets already loaded elsewhere.
  const { data: assetData, loading: basicLoading } = useQuery<{
    asset: { id: string; name: string | null; mediatype: string | null };
  }>(assetBasicInfo, {
    variables: { id },
  });

  // Show skeleton only when no viable name exists yet and at least one source is still loading
  const hasAnyName = !!superViewTitle || !!name || !!assetData?.asset?.name;
  const loading = !hasAnyName && (svLoading || basicLoading);
  const error = svError;

  // SuperView title wins, then explicit name prop, then assetBasicInfo name
  const displayName = getAssetDisplayName({
    name: superViewTitle || name,
    contentObjectName: assetData?.asset?.name,
    id,
  });

  // Resolve media type: prefer prop, fall back to asset's mediatype field
  const resolvedMediatype = mediatype || assetData?.asset?.mediatype || undefined;

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null }>({
    accept: dragType,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      hoverCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop(item) {
      if (!ref.current) return;
      moveCard(item.id, index);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: dragType,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  if (loading)
    return (
      <div className={classes.compactRow}>
        <Skeleton
          variant="rectangular"
          width={32}
          height={22}
          animation="wave"
          sx={{ borderRadius: 0.5, flexShrink: 0 }}
        />
        <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ flex: 1 }} />
      </div>
    );
  if (error) return <div>Error! {error?.message}</div>;

  if (isDragging) {
    return <div ref={ref} data-handler-id={handlerId} className={classes.dragPlaceholder} />;
  }

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`${classes.compactRow} ${flashId === id ? classes.flashRow : ''}`}
      style={{ cursor: 'move' }}
    >
      {overlay}
      <DragIndicatorIcon className={`${classes.dragGrip} ${classes.hoverVisible}`} />
      <span className={classes.indexNumber}>{index + 1}</span>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: click handler is conditionally applied for preview */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: click handler is conditionally applied for preview */}
      <div className={classes.compactThumbnail} onClick={onThumbnailClick}>
        <MediaRouterComponent
          itemId={id}
          shared={false}
          setPlayer={setPlayer}
          videoJsProps={{ options: { controls: false } }}
        />
      </div>
      <span className={classes.mediaTypeIcon}>{getMediaTypeIcon(resolvedMediatype)}</span>
      {onTitleClick ? (
        <button type="button" className={classes.itemTitle} onClick={onTitleClick}>
          {displayName}
        </button>
      ) : (
        <span className={classes.itemTitle}>{displayName}</span>
      )}
      <CloseIcon className={`${classes.removeButton} ${classes.hoverVisible}`} onClick={onRemove} />
    </div>
  );
};

export default PlaylistItemRow;
